import type { SxProps } from '@mui/material';

// Define the mandated colors
const GREEN_COLOR = '#28A766';
const BLUE_COLOR = '#1B94B4';
const DARK_ACCENT_COLOR = '#0e4f5d'; // Darker version of blue for deep hover/shadow
const TEXT_COLOR = '#150c56'; // Keeping the original dark text color for contrast/headers
const LIGHT_BACKGROUND = '#f8f9fa';

// Layout

export const container: SxProps = {
  color: TEXT_COLOR,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 3,
};

export const formPaper: SxProps = {
  maxWidth: '100%',
  width: '100%',
  borderRadius: 4,
  overflow: 'hidden',
};

export const header: SxProps = {
  padding: 4,
  color: TEXT_COLOR,
};

export const headerTitle: SxProps = {
  variant: 'h4',
  fontWeight: '700',
  letterSpacing: '0.5px',
};

export const progressContainer: SxProps = {
  mt: 3,
};

export const progressBar: SxProps = {
  height: 8,
  borderRadius: 4,
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  '& .MuiLinearProgress-bar': {
    // Progress bar color is the GREEN
    backgroundColor: GREEN_COLOR,
    borderRadius: 4,
  },
};

export const questionIndicatorsContainer: SxProps = {
  padding: 3,
  backgroundColor: LIGHT_BACKGROUND,
};

export const questionIndicator: SxProps = {
  width: 32,
  height: 32,
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.875rem',
  fontWeight: '600',
  color: TEXT_COLOR,
  backgroundColor: 'white',
  border: '1px solid #e0e0e0',
  '&.current': {
    // Current indicator is the BLUE
    backgroundColor: BLUE_COLOR,
    color: 'white',
  },
  '&.answered': {
    // Answered indicator is the GREEN
    backgroundColor: GREEN_COLOR,
    color: 'white',
    borderColor: GREEN_COLOR,
  },
};

export const questionContainer: SxProps = {
  padding: 4,
  minHeight: '400px',
  display: 'flex',
  flexDirection: 'column',
};

export const questionNumber: SxProps = {
  display: 'inline-block',
  // Question number uses a light accent from the BLUE
  backgroundColor: 'rgba(27, 148, 180, 0.1)',
  color: BLUE_COLOR,
  borderRadius: '6px',
  padding: '4px 12px',
  fontSize: '0.875rem',
  fontWeight: '600',
  marginBottom: 2,
};

export const questionText: SxProps = {
  fontSize: '1.25rem',
  fontWeight: '600',
  marginBottom: 4,
  lineHeight: 1.4,
};

export const radioGroup: SxProps = {
  '& .MuiFormControlLabel-root': {
    margin: '8px 0',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    transition: 'all 0.2s ease',
    '&:hover': {
      // Radio hover uses the BLUE
      borderColor: BLUE_COLOR,
      backgroundColor: 'rgba(27, 148, 180, 0.04)',
    },
    '&.Mui-checked': {
      // Radio checked uses the GREEN
      borderColor: GREEN_COLOR,
      backgroundColor: 'rgba(40, 167, 102, 0.08)',
    },
  },
};

export const navigationButtons: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: 'auto',
  pt: 3,
};

export const navButton: SxProps = {
  minWidth: '120px',
  padding: '10px 24px',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: '600',
  '&.back': {
    backgroundColor: 'white',
    color: TEXT_COLOR,
    border: '1px solid #e0e0e0',
    '&:hover': {
      backgroundColor: LIGHT_BACKGROUND,
    },
  },
  '&.next': {
    // Next button is the GREEN
    backgroundColor: GREEN_COLOR,
    color: 'white',
    '&:hover': {
      // Next button hover is the BLUE
      backgroundColor: BLUE_COLOR,
    },
  },
  '&.submit': {
    // Submit button is the BLUE
    backgroundColor: BLUE_COLOR,
    color: 'white',
    '&:hover': {
      // Submit button hover is the darker BLUE
      backgroundColor: DARK_ACCENT_COLOR,
    },
  },
};

export const completeContainer: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: 6,
  minHeight: '400px',
};

export const completeIcon: SxProps = {
  width: 80,
  height: 80,
  borderRadius: '50%',
  // Icon background uses light GREEN
  backgroundColor: 'rgba(40, 167, 102, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 3,
  '& svg': {
    // Icon color is the GREEN
    color: GREEN_COLOR,
    width: 40,
    height: 40,
  },
};

export const completeTitle: SxProps = {
  fontSize: '2rem',
  fontWeight: '700',
  marginBottom: 2,
  color: TEXT_COLOR,
};

export const completeText: SxProps = {
  fontSize: '1.1rem',
  color: '#666',
  maxWidth: '500px',
  marginBottom: 4,
  lineHeight: 1.6,
};

// Question Card

export const questionCard: SxProps = {
  padding: 3,
  borderRadius: 2,
  border: '2px solid #e0e0e0',
  transition: 'all 0.3s ease',
  '&:hover': {
    // Card hover uses the GREEN
    borderColor: GREEN_COLOR,
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(40, 167, 102, 0.15)',
  },
};

export const questionCardActive: SxProps = {
  borderColor: TEXT_COLOR,
};

// Form Elements

export const formLabel: SxProps = {
  color: BLUE_COLOR, // Using BLUE for the form label accent
  fontWeight: 600,
  fontSize: '1rem',
  mb: 2,
  '&.Mui-focused': {
    color: BLUE_COLOR,
  },
};

export const radioOption: SxProps = {
  color: 'secondary.main',
  '&.Mui-checked': {
    color: GREEN_COLOR, // Radio checked mark is GREEN
  },
};

export const chip: SxProps = {
  fontWeight: 600,
  paddingX: 2,
  fontSize: '0.9rem',
  transition: 'all 0.3s ease',
};

export const chipSelected: SxProps = {
  // Selected chip is GREEN
  backgroundColor: `${GREEN_COLOR} !important`,
  color: 'white !important',
};

export const chipUnselected: SxProps = {
  backgroundColor: '#f5f5f5',
  color: '#666',
};

// Navigation

export const navigationContainer: SxProps = {
  mt: 4,
};

// Question Header

export const questionHeader: SxProps = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mb: 2,
};

export const questionTitle: SxProps = {
  color: TEXT_COLOR, // Keep dark for readability
  fontWeight: 600,
};

// Submit Button

export const submitButton: SxProps = {
  paddingX: 4,
  paddingY: 1.2,
  fontWeight: 600,
  // Shadow uses the darker BLUE
  boxShadow: `0 4px 14px ${BLUE_COLOR}40`,
  '&:hover': {
    transform: 'translateY(-2px)',
    // Hover shadow uses the darkest blue
    boxShadow: `0 6px 20px ${DARK_ACCENT_COLOR}60`,
  },
  transition: 'all 0.3s ease',
};